/**
 * ConsistencyDataService is a service that ensures data consistency across various data types.
 * It uses dependency injection to get necessary services and performs checks on data entries.
 */
class ConsistencyDataService {
    /**
     * Constructor for ConsistencyDataService.
     * @param {Object} $injector - AngularJS injector service.
     */
    constructor($injector) {
        // 'ngInject'; // Remove this line if present
        if (!isCMS) {
            return;
        }

        this._logIdentifier = 'ConsistencyDataService';
        this.$logger = $injector.get('LogBridgeService');
        this.$logger.info(this, 'initialize');

        this._TaskDataService = null;
        this._translate = null;
        this.$injector = $injector;
    }

    /**
     * Get the TaskDataService instance.
     * @returns {Object} TaskDataService instance.
     */
    getTaskDataService() {
        if (!this._TaskDataService) {
            this._TaskDataService = this.$injector.get('TaskDataService');
        }
        return this._TaskDataService;
    }

    /**
     * Get the $translate service instance.
     * @returns {Object} $translate service instance.
     */
    getTranslateService() {
        if (!this._translate) {
            this._translate = this.$injector.get('$translate');
        }
        return this._translate;
    }

    /**
     * Check and handle task type entries for consistency.
     * @param {Object} entry - The data entry to check.
     * @param {Object} response_objects - The repository of response objects.
     */
    _taskType(entry, response_objects) {
        for (let key in entry.data.sheets) {
            let sheet = entry.data.sheets[key];
            if (angular.isUUID(sheet)) {
                if (!response_objects[sheet]) {
                    console.warn("In Entry", entry, " sheet", sheet, "does not exist in Repository");
                    entry.data.sheets.splice(key, 1);
                    entry.flagAsDirty();
                }
            }
        }
        for (let key in entry.data.questionItemGroups) {
            let questionItemGroup = entry.data.questionItemGroups[key];
            if (angular.isUUID(questionItemGroup)) {
                if (!response_objects[questionItemGroup]) {
                    console.warn("In Entry", entry, " questionItemGroup", questionItemGroup, "does not exist in Repository");
                    entry.data.questionItemGroups.splice(key, 1);
                    entry.flagAsDirty();
                }
            }
        }
    }

    /**
     * Check and handle sheet type entries for consistency.
     * @param {Object} entry - The data entry to check.
     * @param {Object} response_objects - The repository of response objects.
     */
    _sheetType(entry, response_objects) {
        if (angular.isUUID(entry.data.layout)) {
            if (!response_objects[entry.data.layout]) {
                console.warn("In Entry", entry, " layout", entry.data.layout, "does not exist in Repository");
            }
        }
        for (let key in entry.data.questionParts) {
            let questionPart = entry.data.questionParts[key];
            if (angular.isUUID(questionPart)) {
                if (!response_objects[questionPart]) {
                    console.warn("In Entry", entry, " questionItemGroup", questionPart, "does not exist in Repository");
                    entry.data.questionParts.splice(key, 1);
                    entry.flagAsDirty();
                }
            }
        }

        if (!entry.data.journal) {
            if ((!entry.data.previewText) || (angular.isUUID(entry.data.previewText) && !response_objects[entry.data.previewText])) {
                if (!entry.data.previewText) {
                    entry.data.previewText = angular.createUUID();
                    console.warn("In Entry", entry, " previewText does not exist");
                } else {
                    console.warn("In Entry", entry, " previewText", entry.data.previewText, "does not exist in Repository");
                }
                let new_previewText_obj = {
                    "preferences": {
                        "spellchecker_language": null
                    },
                    "content": (entry.data.preRelease ? this.getTranslateService().instant('PREFIX.PRE-RELEASE') : '...Preview Text'),
                    "objectType": "text",
                    "type": "contentObject",
                    "entity_content": {
                        "content": "",
                        "preferences": {
                            "spellchecker_language": null
                        }
                    },
                    "uuid": entry.data.previewText
                };

                entry.context.write(entry.data.previewText, new_previewText_obj);
                let previewText_obj = entry.context.read(entry.data.previewText);
                previewText_obj.flagAsDirty();
                entry.flagAsDirty();
            }

            if (!entry.data.preRelease) {
                for (let key in entry.data.previewImages) {
                    let previewImage = entry.data.previewImages[key];
                    if (angular.isUUID(previewImage)) {
                        if (!response_objects[previewImage]) {
                            console.warn("In Entry", entry, " previewImage", previewImage, "does not exist in Repository");
                            entry.data.previewImages.splice(key, 1);

                            for (let entity_content_key in entry.data.entity_content.previewImages) {
                                let entity_conten_previewImage = entry.data.entity_content.previewImages[entity_content_key];
                                if (previewImage === entity_conten_previewImage) {
                                    entry.data.entity_content.previewImages.splice(entity_content_key, 1);
                                }
                            }

                            entry.flagAsDirty();
                        }
                    }
                }
            }
        }
    }

    /**
     * Check and handle layout type entries for consistency.
     * @param {Object} entry - The data entry to check.
     * @param {Object} response_objects - The repository of response objects.
     */
    _layoutType(entry, response_objects) {
        for (let row_key in entry.data.rows) {
            let row = entry.data.rows[row_key];
            for (let col_key in row.cols) {
                let col = row.cols[col_key];
                for (let list_key in col.list) {
                    let list = col.list[list_key];
                    let container_uuid = list.content;
                    if (angular.isUUID(container_uuid)) {
                        if (!response_objects[container_uuid]) {
                            console.warn("In Entry", entry, " container_uuid", container_uuid, "does not exist in Repository");
                            col.list.splice(list_key, 1);
                            entry.flagAsDirty();
                        }
                    }
                }
            }
        }
    }

    /**
     * Check and handle container type entries for consistency.
     * @param {Object} entry - The data entry to check.
     * @param {Object} response_objects - The repository of response objects.
     */
    _containerType(entry, response_objects) {
        let update_sub_content = (container) => {
            for (let key in container) {
                let tab = container[key];
                if (!angular.isUUID(tab.content)) {
                    console.warn("In Entry", entry, " Tab Sub-Container", tab.content, "is not a UUID");

                    let new_uuid = angular.createUUID();
                    tab.content.uuid = new_uuid;
                    entry.context.write(new_uuid, tab.content);
                    tab.content = new_uuid;
                    let contentObj = entry.context.read(tab.content);
                    contentObj.flagAsDirty();
                } else if (angular.isUUID(tab.content)) {
                    if (!response_objects[tab.content]) {
                        console.warn("In Entry", entry, " Tab Sub-Container", tab.content, "does not exist in Repository");
                        container.splice(key, 1);
                        entry.flagAsDirty();
                    }
                }
            }
        };
        if (entry.data.showTabs || entry.data.showCarousels || entry.data.showIaaContainer) {
            if (entry.data.showTabs) {
                update_sub_content(entry.data.tabs);
            } else if (entry.data.showCarousels) {
                update_sub_content(entry.data.carousels);
            } else if (entry.data.showIaaContainer) {
                update_sub_content(entry.data.iaaContainer);
            }
        }

        for (let key in entry.data.objects) {
            let object = entry.data.objects[key];
            if (!angular.isUUID(object)) {
                console.warn("In Entry", entry, " object", object, "is not a UUID");

                let new_uuid = angular.createUUID();
                object.uuid = new_uuid;
                entry.context.write(new_uuid, object);
                entry.flagAsDirty();
                entry.data.objects.splice(key, 1, new_uuid);
            } else if (angular.isUUID(object)) {
                if (!response_objects[object]) {
                    console.warn("In Entry", entry, " object", object, "does not exist in Repository");
                    entry.data.objects.splice(key, 1);
                    entry.flagAsDirty();
                }
            }
        }

        if (angular.isUUID(entry.data.questionPart)) {
            if (!response_objects[entry.data.questionPart]) {
                console.warn("In Entry", entry, " questionPart", entry.data.questionPart, "does not exist in Repository");
                entry.data.questionPart = null;
                entry.flagAsDirty();
            }
        }
    }

    /**
     * Check and handle content object type image entries for consistency.
     * @param {Object} entry - The data entry to check.
     * @param {Object} response_objects - The repository of response objects.
     */
    _contentObjectType_image(entry, response_objects) {
        if (angular.isUUID(entry.data.contentReference)) {
            if (!response_objects[entry.data.contentReference]) {
                console.warn("In Entry", entry, " contentReference", entry.data.contentReference, "does not exist in Repository");
                entry.data.contentReference = false;
                if (entry.data.entity_content && angular.isUUID(entry.data.entity_content.contentReference)) {
                    delete entry.data.entity_content['contentReference'];
                }
                entry.flagAsDirty();
            }
        }
    }

    /**
     * Check and handle question part type entries for consistency.
     * @param {Object} entry - The data entry to check.
     * @param {Object} response_objects - The repository of response objects.
     */
    _questionPartType(entry, response_objects) {
        if (angular.isUUID(entry.data.questionItemGroup)) {
            if (!response_objects[entry.data.questionItemGroup]) {
                console.warn("In Entry", entry, " questionItemGroup", entry.data.questionItemGroup, "does not exist in Repository");
                entry.data.questionItemGroup = null;
                entry.flagAsDirty();
            }
        }
    }

    /**
     * Check the consistency of an entry based on its type.
     * @param {Object} entry - The data entry to check.
     * @param {Object} response_objects - The repository of response objects.
     */
    isConsistency(entry, response_objects) {
        if (entry.data.type === 'task') {
            this._taskType(entry, response_objects);
        } else if (entry.data.type === 'sheet') {
            this._sheetType(entry, response_objects);
        } else if (entry.data.type === 'layout') {
            this._layoutType(entry, response_objects);
        } else if (entry.data.type === 'container') {
            this._containerType(entry, response_objects);
        } else if (entry.data.type === 'contentObject') {
            if (entry.data.objectType === 'image') {
                this._contentObjectType_image(entry, response_objects);
            }
        } else if (entry.data.type === 'questionPart') {
            this._questionPartType(entry, response_objects);
        }
    }
}

// Add explicit dependency injection
ConsistencyDataService.$inject = ['$injector'];

export default ConsistencyDataService;